import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { ShieldCheck, XCircle, Award } from "lucide-react";

export default function VerifyPage() {
  const { number } = useParams();
  const { t, lang, setLang } = useI18n();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/certificates/verify/${number}`)
      .then((r) => setCert(r.data))
      .catch(() => setError(lang === "it" ? "Certificato non trovato o non valido." : "Certificate not found or invalid."));
  }, [number, lang]);

  const expired = cert?.status === "expired";
  const ok = cert && cert.verified && !expired;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_attestguard/artifacts/3wuvsw34_FORMATO-WEB-NERO.jpg"
              alt="Sicurgarda"
              className="h-8 w-auto"
            />
          </Link>
          <button onClick={() => setLang(lang === "it" ? "en" : "it")} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground btn-lift">
            {lang.toUpperCase()}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{lang === "it" ? "Verifica pubblica" : "Public verification"}</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">
            {lang === "it" ? "Verifica attestato" : "Certificate verification"}
          </h1>
          <p className="text-muted-foreground text-sm mt-3">
            {lang === "it" ? "Attestato n." : "Certificate no."} <span className="font-mono font-semibold text-foreground">{number}</span>
          </p>
        </div>

        {error && (
          <div className="bg-card border border-destructive/40 rounded-lg p-8 flex items-start gap-4">
            <XCircle className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">{lang === "it" ? "Non verificato" : "Not verified"}</div>
              <div className="text-sm text-muted-foreground mt-1">{error}</div>
            </div>
          </div>
        )}

        {cert && (
          <>
            <div className={`bg-card border-l-4 border-y border-r border-border rounded-lg p-8 ${ok ? "border-l-[hsl(var(--success))]" : "border-l-destructive"} card-hover`} data-testid="verify-result">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-md grid place-items-center ${ok ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]" : "bg-destructive/10 text-destructive"}`}>
                  {ok ? <ShieldCheck className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("status")}</div>
                  <div className="text-xl font-bold mt-1">
                    {ok
                      ? (lang === "it" ? "Attestato valido e autentico" : "Certificate valid & authentic")
                      : (lang === "it" ? "Attestato scaduto" : "Certificate expired")}
                  </div>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                <Field label={lang === "it" ? "Corsista" : "Recipient"} value={cert.worker_name} />
                <Field label={lang === "it" ? "Cod. Fiscale" : "Tax code"} value={cert.worker_tax_code_masked || "—"} mono />
                <Field label={lang === "it" ? "Azienda" : "Company"} value={cert.company_name} />
                <Field label={lang === "it" ? "Soggetto formatore" : "Training body"} value={cert.training_body || "—"} />
                <Field label={t("course_title")} value={cert.course_title} />
                <Field label={t("course_hours")} value={cert.course_hours ? `${cert.course_hours} h` : "—"} />
                <Field label={t("trainer")} value={cert.trainer_name || "—"} />
                <Field label={t("tutor")} value={cert.tutor_name || "—"} />
                <Field label={t("issue_date")} value={cert.issue_date} />
                <Field label={t("expiry_date")} value={cert.expiry_date || (lang === "it" ? "Nessuna" : "None")} />
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-6 text-center">
              {lang === "it"
                ? "Questa pagina è una verifica pubblica generata dal sistema Sicurgarda. I dati sono minimizzati per rispetto della privacy."
                : "This is a public verification generated by Sicurgarda. Data is minimized to protect privacy."}
            </p>
          </>
        )}

        {!cert && !error && <div className="text-muted-foreground">{t("loading")}</div>}
      </main>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono" : "font-medium"}`}>{value}</div>
    </div>
  );
}
